import { getUniqueId, EventEmitterWrapper } from '@umu/umu-util';
import './index.scss';
import { isWeChat } from '@umu/umu-ua';

let manager: PlayerManager | null = null;

export type TMediaType = 'video' | 'audio';

export interface IMediaOptions {
    isPrimary?: boolean; // 是否页面中的主播放器，与其它效果不同的时，可以被恢复播放
    autoplay?: boolean;
    controls?: boolean;
    loop?: boolean;
    file: string; // 播放文件路径
    volume?: number; // 音量改成100分制
    lastPlayedTime?: number;
    fileId?: string;
}

// http://www.w3school.com.cn/html5/html5_ref_audio_video_dom.asp
export enum EMediaEvent {
    playing = 'playing',
    canplay = 'canplay',
    waiting = 'waiting',
    progress = 'progress',
    stalled = 'stalled',
    loadstart = 'loadstart',
    timeupdate = 'timeupdate',
    ratechange = 'ratechange',
    pause = 'pause',
    ended = 'ended',
    error = 'error',
    seeking = 'seeking',
    touchstart = 'touchstart',
    touchend = 'touchend',
    pointerdown = 'pointerdown',
    loadedmetadata = 'loadedmetadata',
}

export type TMediaEvent =
    | 'playing'
    | 'canplay'
    | 'waiting'
    | 'progress'
    | 'stalled'
    | 'loadstart'
    | 'timeupdate'
    | 'seeking'
    | 'ratechange'
    | 'pause'
    | 'ended'
    | 'error'
    | 'loadedmetadata';

interface IMediaConfig {
    uniqueId: string;
    type: TMediaType;
    options: IMediaOptions;
    isPrimary: boolean;
    isLoaded: boolean;
    isPlaying: boolean;
    lastPlayedTime: number;
}
const MediaList: { [key: string]: IMediaConfig } = {};

// 获取视频加载状态
const getVideoElementState = (videoElement: HTMLVideoElement) => {
    return {
        src: videoElement?.src,
        // 当前缓冲范围，返回 TimeRanges 对象
        buffered: videoElement?.buffered,
        // 视频是否已经加载完成
        readyState: videoElement?.readyState,
        // 当前网络状态
        networkState: videoElement?.networkState,
        // 是否正在加载数据
        seeking: videoElement?.seeking,
        // 视频是否已加载足够数据可以开始播放
        // 0 没有关于音频/视频是否就绪的信息
        // 1 已获取元数据
        // 2 当前播放位置的数据是可用的，但没有足够的数据来播放下一帧/毫秒
        // 3 当前及至少下一帧的数据是可用的
        // 4 可用数据足够开始播放
        canPlay: videoElement?.readyState >= 3,
        ended: videoElement?.ended,
        paused: videoElement?.paused,
        volume: videoElement?.volume,
        currentTime: videoElement?.currentTime,
        duration: videoElement?.duration,
    };
};

class PlayerManager extends EventEmitterWrapper {
    private isVideoCanPlay: boolean = false;
    private isPendingPlay: boolean = false;
    public static getInstance() {
        if (!manager) {
            manager = new PlayerManager();
        }
        return manager;
    }

    private playingUniqueId: string = '';
    // private audioPlayer: HTMLAudioElement | null = null;
    private videoPlayerMap: { [key: string]: HTMLVideoElement } = {};

    public getPlayer(type: TMediaType, uniqueId: string) {
        if (type === 'video') {
            if (!this.videoPlayerMap[uniqueId]) {
                this.videoPlayerMap[uniqueId] = document.createElement('video');
                this.initPlayer(this.videoPlayerMap[uniqueId], type);
                this.bindEvent(this.videoPlayerMap[uniqueId], uniqueId);
            }
            return this.videoPlayerMap[uniqueId];
        }

        // 目前没有看到有用到 audio 的地方，先注释掉
        // if (type === 'audio') {
        //     if (!this.audioPlayer) {
        //         this.audioPlayer = document.createElement('audio');
        //         this.audioPlayer.className = 'audio-player-el';
        //         this.bindEvent(this.audioPlayer);
        //     }
        //     return this.audioPlayer;
        // }
        console.error('Player type error, unable to get instance');
        return null;
    }

    public add(type: TMediaType, options: IMediaOptions) {
        const uniqueId = getUniqueId(type + '-');
        MediaList[uniqueId] = {
            uniqueId,
            type,
            options,
            isPrimary: !!options.isPrimary,
            isLoaded: false,
            isPlaying: false,
            lastPlayedTime: options.lastPlayedTime || 0,
        };
        return uniqueId;
    }

    public getCurrentMediaConfig() {
        const uniqueId = this.playingUniqueId;
        const config = MediaList[uniqueId];
        return config;
    }

    private initPlayerOptions(config: IMediaConfig, uniqueId: string) {
        const player = this.getPlayer(config.type, uniqueId);
        if (!player) {
            return;
        }

        const options = config.options;

        player.autoplay = !!options.autoplay;
        player.controls = !!options.controls;
        player.loop = !!options.loop;
        player.volume = (options.volume || 0) / 100;

        // 音频重复设置没问题，视频重复设置会闪一次，因此控制一下
        // HLS 的模式要排除掉
        if (!options.fileId && player.src !== options.file) {
            player.src = options.file;
        }
    }

    public bindPlayerWithUniqueId(player: HTMLVideoElement, uniqueId: string) {
        this.videoPlayerMap[uniqueId] = player;
    }

    public load(uniqueId: string) {
        const config = MediaList[uniqueId];

        // 只有页面中的主播放器才可预加载
        if (!config.isPrimary) {
            return;
        }

        if (config.options.fileId) {
            this.playingUniqueId = uniqueId;
            return;
        }
        const player = this.getPlayer(config.type, uniqueId);

        if (player) {
            if (!config.isLoaded) {
                this.initPlayerOptions(config, uniqueId);
                config.isLoaded = true;
            }
        }
        this.playingUniqueId = uniqueId;

        if (isWeChat) {
            this.isVideoCanPlay = true;
        }

        return player;
    }

    public play(uniqueId: string) {
        this.stopOtherPlayer(uniqueId);
        this.playNext(uniqueId);
    }

    public async playNext(uniqueId: string) {
        const oldConfig = this.getCurrentMediaConfig();

        this.playingUniqueId = uniqueId;
        const config = this.getCurrentMediaConfig();

        if (!config) {
            return;
        }

        if (!oldConfig || config.uniqueId !== oldConfig.uniqueId) {
            this.initPlayerOptions(config, uniqueId);
        }

        const player = this.getPlayer(config.type, uniqueId);
        if (!player) {
            return;
        }
        if (this.isVideoCanPlay) {
            try {
                await player.play();
                config.isPlaying = true;
            } catch (error: any) {
                console.error(
                    '[PlayerManager]:[playNext] play error:',
                    error,
                    config,
                    JSON.stringify(getVideoElementState(player)),
                );
            }
        } else {
            this.isPendingPlay = true;
        }
    }

    private async autoPlayIfNeeded(uniqueId: string) {
        if (!this.isPendingPlay) {
            return;
        }

        const config = this.getCurrentMediaConfig();

        if (!config) {
            return;
        }

        this.isPendingPlay = false;

        const player = this.getPlayer(config.type, uniqueId);
        if (!player) {
            return;
        }

        try {
            await player.play();
        } catch (error) {
            console.error('[PlayerManager]:[autoPlayIfNeeded] play error:', error);
        }
        config.isPlaying = true;
        // player.currentTime = config.isPrimary ? config.lastPlayedTime : 0;
    }

    public stopOtherPlayer(nextId: string) {
        const oldConfig = this.getCurrentMediaConfig();
        if (!oldConfig) {
            return;
        }

        if (oldConfig.uniqueId !== nextId) {
            this.pause(oldConfig.uniqueId, true);
            const player = this.getPlayer(oldConfig.type, oldConfig.uniqueId);
            if (player) {
                if (oldConfig.isPrimary) {
                    oldConfig.lastPlayedTime = player.currentTime;
                } else {
                    player.currentTime = 0;
                }
            }
        }
    }

    public pause(uniqueId: string, isInterrupted?: boolean) {
        const config = MediaList[uniqueId];
        const player = this.getPlayer(config.type, uniqueId);
        if (!player || player.paused) {
            return;
        }
        config.lastPlayedTime = player.currentTime;
        if (!isInterrupted) {
            config.isPlaying = false;
        }
        player.pause();
    }

    public resetPrimaryPlayer() {
        /* 找到主播放器，如果它是在被播放过程中暂停的，就恢复播放 */
        for (const uniqueId in MediaList) {
            if (MediaList.hasOwnProperty(uniqueId)) {
                const config = MediaList[uniqueId];
                if (config.isPrimary && config.isPlaying) {
                    this.play(uniqueId);
                }
            }
        }
    }

    public initPlayer(player: HTMLAudioElement | HTMLVideoElement, type: TMediaType) {
        if (type === 'video') {
            player.setAttribute('preload', 'auto');
            player.setAttribute('playsinline', 'true');
            player.setAttribute('webkit-playsinline', 'true');
            player.setAttribute('controlsList', 'nodownload noplaybackrate');
            player.setAttribute('x5-video-player-type', 'h5');
            player.setAttribute('x5-video-player-fullscreen', 'true');
            player.setAttribute('x5-video-orientation', 'landscape|portrait');
        }
    }

    public removeAttribute = (player: HTMLAudioElement | HTMLVideoElement, attributeName: string) => {
        player.removeAttribute(attributeName);
    };

    public bindEvent(player: HTMLAudioElement | HTMLVideoElement, uniqueId: string) {
        for (const event in EMediaEvent) {
            if (!EMediaEvent.hasOwnProperty(event)) {
                return;
            }
            player.addEventListener(event, (...args: any[]) => {
                this.emit(event + '-' + this.playingUniqueId, args);
                if (event === EMediaEvent.ended) {
                    this.pause(this.playingUniqueId);
                    this.resetPrimaryPlayer();
                } else if (event === EMediaEvent.canplay || event === EMediaEvent.loadedmetadata) {
                    this.isVideoCanPlay = true;
                    this.autoPlayIfNeeded(uniqueId);
                }
            });
        }
    }
}

export default PlayerManager.getInstance();
